/** مهلة تقديم نموذج س1: أسبوعان من تاريخ الالتحاق. */
export const INSURANCE_WINDOW_DAYS = 14;

export const INSURANCE_STATUSES = {
  DRAFT: "مسودة",
  READY: "جاهز للتسليم",
  SUBMITTED: "تم التسليم للمكتب",
  REGISTERED: "تم التسجيل",
};

export const ATTACHMENT_KINDS = [
  {
    value: "BIRTH_DATE",
    label: "مستند تاريخ الميلاد المميكن",
    required: true,
  },
  {
    value: "QUALIFICATION",
    label: "صورة المؤهل الدراسي",
    required: true,
  },
  {
    value: "PROFESSIONAL_UNION",
    label: "ما يفيد الاشتراك بالنقابة المهنية",
    required: "professional",
  },
  {
    value: "NATIONAL_ID",
    label: "صورة بطاقة الرقم القومي",
    required: true,
  },
  {
    value: "APPOINTMENT_OR_CONTRACT",
    label: "قرار التعيين أو بيان بدء الاشتراك أو عقد العمل",
    required: true,
  },
  {
    value: "WORK_RECEIPT",
    label: "إقرار استلام العمل",
    required: false,
    hint: "إن وجد",
  },
  {
    value: "MEDICAL_111",
    label: "تقرير اللياقة الطبية — نموذج 111",
    required: true,
  },
  {
    value: "FORM_3",
    label: "صحيفة البيانات الأساسية — نموذج 3",
    required: false,
    hint: "يُرفق عند وجود مدد سابقة، ويمكن طباعته من الشاشة",
  },
  {
    value: "FORM_4",
    label: "بيان المدد السابقة أو المعاش الآخر — نموذج 4",
    required: false,
    hint: "يُحرر من الشاشة",
  },
  {
    value: "FORM_5",
    label: "بيانات التغطية التأمينية — نموذج 5",
    required: false,
    hint: "يُحرر ويُطبع من الشاشة",
  },
];

const PROFESSIONAL_TITLE =
  /مهندس|طبيب|طبيبة|محام|محاسب|صيدل/;

export function isProfessionalTitle(jobTitle = "") {
  return PROFESSIONAL_TITLE.test(String(jobTitle || ""));
}

export function addCalendarDays(isoDate, days) {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}/.test(String(isoDate))) return null;
  const [year, month, day] = String(isoDate).slice(0, 10).split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function calendarDaysBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return null;
  const [fy, fm, fd] = String(fromIso).slice(0, 10).split("-").map(Number);
  const [ty, tm, td] = String(toIso).slice(0, 10).split("-").map(Number);
  const from = Date.UTC(fy, fm - 1, fd);
  const to = Date.UTC(ty, tm - 1, td);
  return Math.round((to - from) / 86400000);
}

export function todayIso(now = new Date()) {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function insuranceDeadline(hireDate) {
  return addCalendarDays(hireDate, INSURANCE_WINDOW_DAYS);
}

/**
 * @returns {{ deadlineDate: string|null, daysLeft: number|null, tone: string }}
 * tone: done | overdue | soon | ok | unknown
 */
export function describeDeadline(hireDate, status, now = new Date()) {
  if (status === "SUBMITTED" || status === "REGISTERED") {
    return {
      deadlineDate: insuranceDeadline(hireDate),
      daysLeft: null,
      tone: "done",
    };
  }
  const deadlineDate = insuranceDeadline(hireDate);
  if (!deadlineDate) {
    return { deadlineDate: null, daysLeft: null, tone: "unknown" };
  }
  const daysLeft = calendarDaysBetween(todayIso(now), deadlineDate);
  let tone = "ok";
  if (daysLeft < 0) tone = "overdue";
  else if (daysLeft <= 3) tone = "soon";
  return { deadlineDate, daysLeft, tone };
}

export function deadlineLabel(info) {
  if (!info) return "—";
  if (info.tone === "done") return "تم التقديم";
  if (info.tone === "unknown") return "بدون تاريخ التحاق";
  if (info.tone === "overdue") {
    return `متأخر ${Math.abs(info.daysLeft)} يوم`;
  }
  if (info.daysLeft === 0) return "آخر يوم في المهلة";
  return `متبقي ${info.daysLeft} يوم`;
}

function text(value) {
  return String(value ?? "").trim();
}

function hasAttachment(attachments, kind) {
  return (attachments || []).some((item) => item.kind === kind);
}

function hasPeriod(form3) {
  return (form3?.periods || []).some(
    (row) => text(row?.employerName) && text(row?.from) && text(row?.to),
  );
}

/**
 * نواقص ملف الاشتراك قبل تسليمه لمكتب التأمينات.
 * التوقيعات مطلوبة على الأصل الورقي قبل اعتبار الملف جاهزاً.
 */
export function assessInsuranceFile({
  form1 = {},
  form3 = {},
  form4 = {},
  form5 = {},
  attachments = [],
  jobTitle = "",
} = {}) {
  const missing = [];
  const title = text(form1.jobTitle) || text(jobTitle);

  if (!text(form1.establishmentName)) missing.push("اسم المنشأة في نموذج 1");
  if (!text(form1.establishmentNumber)) missing.push("رقم المنشأة في نموذج 1");
  if (!text(form1.insuranceOffice)) missing.push("مكتب التأمينات في نموذج 1");
  if (!text(form1.fullName)) missing.push("اسم المؤمن عليه في نموذج 1");
  if (!/^\d{14}$/.test(text(form1.nationalId))) {
    missing.push("الرقم القومي (14 رقماً) في نموذج 1");
  }
  if (!text(form1.birthDate)) missing.push("تاريخ الميلاد في نموذج 1");
  if (!text(form1.gender)) missing.push("النوع في نموذج 1");
  if (!text(form1.qualification)) missing.push("المؤهل الدراسي في نموذج 1");
  if (!title) missing.push("المهنة في نموذج 1");
  if (!text(form1.subscriptionStartDate)) {
    missing.push("تاريخ بدء الاشتراك في نموذج 1");
  }
  if (form1.basicWage === "" || form1.basicWage === null || form1.basicWage === undefined) {
    missing.push("الأجر الأساسي في نموذج 1");
  }
  if (!text(form1.authorizedPersonName)) {
    missing.push("اسم صاحب العمل أو المفوض في نموذج 1");
  }
  if (!form1.insuredSigned) missing.push("توقيع المؤمن عليه على الأصل");
  if (!form1.employerSigned) missing.push("توقيع صاحب العمل أو المفوض على الأصل");
  if (!form1.stamped) missing.push("ختم المنشأة على الأصل");

  const previous = Boolean(form1.hasPreviousPeriods || form4.hasPreviousPeriods);
  if (previous && !hasPeriod(form3)) {
    missing.push("مدد سابقة في نموذج 3");
  }
  if (form4.hasPreviousPeriods == null && form1.hasPreviousPeriods == null) {
    missing.push("بيان وجود مدد اشتراك سابقة في نموذج 4");
  }
  if (form4.hasOtherPension == null) {
    missing.push("بيان استحقاق معاش آخر في نموذج 4");
  }
  if (form4.hasOtherPension && !text(form4.otherPensionType)) {
    missing.push("نوع المعاش الآخر في نموذج 4");
  }

  if (!form5.oldAge) missing.push("تغطية الشيخوخة والعجز والوفاة في نموذج 5");
  if (!form5.workInjury) missing.push("تغطية إصابات العمل في نموذج 5");
  if (!text(form5.subscriptionStartDate) && !text(form1.subscriptionStartDate)) {
    missing.push("تاريخ بدء التغطية في نموذج 5");
  }

  for (const kind of ATTACHMENT_KINDS) {
    const needed =
      kind.required === true ||
      (kind.required === "professional" && isProfessionalTitle(title));
    if (needed && !hasAttachment(attachments, kind.value)) {
      missing.push(kind.label);
    }
  }

  return { missing, complete: missing.length === 0, previous, professional: isProfessionalTitle(title) };
}

export function emptyPeriod() {
  return {
    employerName: "",
    establishmentNumber: "",
    from: "",
    to: "",
    wage: "",
    endReason: "",
  };
}

export function buildDraftFromEmployee(employee = {}, officeDefaults = {}) {
  const hireDate = employee.joinDate || employee.hireDate || "";
  const wage =
    employee.salary === 0 || employee.salary
      ? String(employee.salary)
      : "";
  return {
    status: "DRAFT",
    insuranceNumber: employee.socialInsuranceNumber || "",
    submissionNotes: "",
    form1: {
      establishmentName: officeDefaults.establishmentName || "",
      establishmentNumber: officeDefaults.establishmentNumber || "",
      establishmentAddress: officeDefaults.establishmentAddress || "",
      activity: officeDefaults.activity || "",
      governorate: officeDefaults.governorate || "",
      insuranceOffice: officeDefaults.insuranceOffice || "",
      fullName: employee.legalFullName || employee.name || "",
      nationalId: employee.nationalId || "",
      birthDate: employee.birthDate || "",
      birthPlace: "",
      gender: employee.gender || "",
      nationality: "مصري",
      maritalStatus: employee.maritalStatus || "",
      address: employee.address || "",
      phone: employee.phone || "",
      qualification: employee.educationLevel || "",
      jobTitle: employee.jobTitle || "",
      hireDate,
      subscriptionStartDate: hireDate,
      employmentType: "PERMANENT",
      basicWage: wage,
      variableWage: "",
      totalWage: wage,
      hasPreviousPeriods: false,
      authorizedPersonName: officeDefaults.authorizedPersonName || "",
      insuredSigned: false,
      employerSigned: false,
      stamped: false,
    },
    form3: {
      insuranceNumber: employee.socialInsuranceNumber || "",
      periods: [],
    },
    form4: {
      hasPreviousPeriods: false,
      hasOtherPension: false,
      otherPensionType: "",
      otherPensionSource: "",
      otherPensionStartDate: "",
      declaration:
        "أقر بصحة بيانات مدد الاشتراك السابقة ومدى استحقاقي معاشاً آخر، وأتحمل المسؤولية عن أي بيان غير مطابق.",
    },
    form5: {
      oldAge: true,
      oldAgeStart: hireDate,
      workInjury: true,
      workInjuryStart: hireDate,
      sickness: false,
      sicknessStart: "",
      unemployment: false,
      unemploymentStart: "",
      basicWage: wage,
      variableWage: "",
      subscriptionStartDate: hireDate,
    },
    attachments: [],
  };
}

export const GENDER_OPTIONS = [
  { value: "MALE", label: "ذكر" },
  { value: "FEMALE", label: "أنثى" },
];

export const EMPLOYMENT_TYPES = [
  { value: "PERMANENT", label: "دائم" },
  { value: "TEMPORARY", label: "مؤقت" },
  { value: "SEASONAL", label: "موسمي" },
  { value: "CASUAL", label: "عرضي" },
];

export const MARITAL_OPTIONS = [
  { value: "SINGLE", label: "أعزب" },
  { value: "ENGAGED", label: "خاطب" },
  { value: "MARRIED", label: "متزوج" },
  { value: "SEPARATED", label: "منفصل" },
];

export const BLANK_FORMS = [
  {
    href: "/insurance-forms/form-1.pdf",
    label: "النموذج الرسمي رقم 1 — طلب اشتراك مؤمن عليه",
  },
  {
    href: "/insurance-forms/form-3.pdf",
    label: "النموذج الرسمي رقم 3 — صحيفة البيانات الأساسية",
  },
  {
    href: "/insurance-forms/form-5.pdf",
    label: "النموذج الرسمي رقم 5 — بيانات التغطية التأمينية",
  },
];
